<template>
  <div
    ref="menuRef"
    class="tc-grid-menu"
    role="dialog"
    aria-label="Column settings"
    data-testid="grid-column-menu"
    @keydown.esc.stop="emit('close')"
  >
    <div class="tc-grid-menu-title">Columns</div>
    <label
      v-for="col in columns"
      :key="col.key"
      class="tc-grid-menu-item"
      :class="{ 'tc-grid-menu-item--locked': col.locked }"
    >
      <input
        type="checkbox"
        class="form-check-input mt-0"
        :checked="!col.hidden"
        :disabled="col.locked"
        :data-testid="`grid-column-toggle-${col.key}`"
        @change="emit('toggle', col.key, !($event.target as HTMLInputElement).checked)"
      />
      <span>{{ col.label }}</span>
      <span v-if="col.locked" class="tc-grid-menu-note">Always shown</span>
    </label>
    <hr class="tc-grid-menu-rule" />
    <button
      type="button"
      class="dropdown-item"
      data-testid="grid-column-reset"
      @click="emit('reset')"
    >Reset columns</button>
    <div class="tc-grid-menu-foot">Saved in this browser{{ narrow ? ' for narrow screens' : '' }}.</div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import type { GridColumnKey } from '../composables/useGridColumns';

const props = defineProps<{
  columns: { key: GridColumnKey; label: string; locked: boolean; hidden: boolean }[];
  narrow: boolean;
  // The gear that opened the menu: a click on it toggles, so it is not "outside".
  anchor?: HTMLElement | null;
}>();

const emit = defineEmits<{
  toggle: [key: GridColumnKey, hidden: boolean];
  reset: [];
  close: [];
}>();

const menuRef = ref<HTMLElement | null>(null);

function onDocPointer(e: PointerEvent): void {
  const t = e.target as Node | null;
  if (!t || menuRef.value?.contains(t) || props.anchor?.contains(t)) return;
  emit('close');
}

onMounted(async () => {
  document.addEventListener('pointerdown', onDocPointer);
  await nextTick();
  menuRef.value?.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
});

onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));
</script>
